import {
  ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnInit, OnChanges, SimpleChanges, ViewChild
} from '@angular/core';
import { formatDate } from '@angular/common';
import { BsModalRef, ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ModalWrapperService } from 'src/app/services/modal-wrapper.service';
import { CommunicationCenterService } from 'src/app/services/operations/communication-center.service';
import { CalltrackerService } from 'src/app/services/operations/calltracker.service';
import { GeneralService } from 'src/app/services/general.service';
import { StoreService } from 'src/app/services/stores.service';
import { EPhoneSystem, EUserFunctions } from 'src/app/Shared/enum';
import {
  ICallCategory, ICallTag, ICommCallCategoryOrTagAssociation, ICustomerCallLogItem, ICustomerCallLogResult, ICustomerCallLogRowSaveResult
} from 'src/app/Shared/models/CommunicationCenter';
import { BaseComponent } from 'src/app/Shared/base.component';
import { callDirectionArrow, callScoreClass, callSentimentClass, formatCallDuration, formatCallerId } from './call-log-format';
import { RelinkCallComponent } from 'src/app/Shared/relink-call/relink-call.component';
import { filter, take, takeUntil } from 'rxjs/operators';

interface ICallLogColumnFilters {
  date: string;
  direction: string;
  duration: string;
  sentiment: string;
  category: string;
  tags: string;
  agent: string;
  aiScore: string;
  humanScore: string;
  storeName: string;
  adSource: string;
  callerId: string;
  trackingNumber: string;
}

// The row being edited inline and its last saved values - compared on leaving the row to decide whether to save,
// and restored if the save fails.
interface ICallLogActiveRow {
  item: ICustomerCallLogItem;
  savedCategories: ICommCallCategoryOrTagAssociation[];
  savedTags: ICommCallCategoryOrTagAssociation[];
  savedHumanScore: number | null;
}

// The independently saved parts of a row, in grid column order - also the names the save toasts use.
type CallLogRowPart = 'Category/Reason' | 'Tags' | 'Human Score';

// Keys the Human Score box accepts besides digits - everything else (letters, '-', '+', '.', 'e') is blocked.
const SCORE_EDIT_KEYS = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter', 'Escape'];

// Communication Center - Epic 4: redesigned Prospect/Customer Call Log (Recording Access), scoped to one
// tenant/prospect. Row expand reuses the existing CommCallSummaryComponent (Epic 2 Story 2.5/2.6/2.7) for
// playback + inline transcript (Story 4.2).
@Component({
  selector: 'app-prospect-customer-call-log',
  templateUrl: './prospect-customer-call-log.component.html',
  styleUrls: ['./prospect-customer-call-log.component.scss'],
  standalone: false
})
export class ProspectCustomerCallLogComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() tenantId: number;
  @Input() facilityId: number;

  @ViewChild('ProspectCustomerCallLog', { static: true }) callLogModal: ModalDirective;
  @ViewChild('CallDetailModal', { static: true }) callDetailModal: ModalDirective;
  @ViewChild('rowPicker') rowPicker: ElementRef<HTMLSelectElement>;
  @ViewChild('scoreInput') scoreInput: ElementRef<HTMLInputElement>;

  loading = false;
  result: ICustomerCallLogResult | null = null;
  rows: ICustomerCallLogItem[] = [];

  callType: 'All' | 'Calls' | 'Voicemails' = 'All';
  sortDescending = true;
  columnFilters: ICallLogColumnFilters = this.emptyColumnFilters();

  callCategories: ICallCategory[] = [];
  callTags: ICallTag[] = [];
  editRight = false;

  // FR-23 inline edit: one row at a time; its changes stay pending until the user leaves the row.
  activeRow: ICallLogActiveRow | null = null;
  openPicker: 'category' | 'tag' | null = null;
  scoreEditing = false;
  scoreValue: number | null = null;
  scoreBlocked = false;

  selectedCall: ICustomerCallLogItem | null = null;
  private permissionsReady = false;
  private loadRequested = false;
  private relinkModalRef: BsModalRef;

  constructor(private communicationCenterService: CommunicationCenterService,
              private calltrackerService: CalltrackerService,
              private modalWrapper: ModalWrapperService,
              private generalService: GeneralService,
              private storeService: StoreService,
              private toastr: ToastrService,
              private cdr: ChangeDetectorRef) { super(); }

  ngOnInit(): void {
    if (this.generalService.UserPermissions?.Functions) {
      this.initialize();
    } else {
      this.generalService.userGroupPermissions.pipe(
        filter(() => !!this.generalService.UserPermissions?.Functions),
        take(1),
        takeUntil(this.unsubscribeOnDestroy$)
      ).subscribe(() => this.initialize());
    }
  }

  private initialize(): void {
    this.permissionsReady = true;
    const callRecordingRights = this.callRecordingRights();
    this.editRight = callRecordingRights.EditRight;
    if (callRecordingRights.ViewRight) {
      this.loadCategoriesAndTags();
    }
    if (this.loadRequested) {
      this.load();
    } else {
      this.loadIfAllowed();
    }
  }

  // This popup is governed by Customer Call Recordings alone - Communication Center rights don't apply here.
  private callRecordingRights() {
    return this.generalService.getUserPermissionsForModule(EUserFunctions['Customer Call Recordings']);
  }

  private loadCategoriesAndTags(): void {
    this.communicationCenterService.getCallCategories().pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (categories) => this.callCategories = categories,
      error: () => this.toastr.error('Failed to load call categories.', 'Error')
    });
    this.communicationCenterService.getCallTags().pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (tags) => this.callTags = tags,
      error: () => this.toastr.error('Failed to load call tags.', 'Error')
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.permissionsReady && (changes.tenantId || changes.facilityId)) {
      this.loadIfAllowed();
    }
  }

  private loadIfAllowed(): void {
    this.result = null;
    this.rows = [];
    if (this.tenantId) {
      if (this.callRecordingRights().ViewRight) {
        this.load();
      }
    }
  }

  open(): void {
    // Drop the previous customer's data so the heading/grid never flash stale rows while this one loads.
    this.result = null;
    this.rows = [];
    this.columnFilters = this.emptyColumnFilters();
    this.resetEditState();
    // Only the page loader shows until the data (and so the tenant/unit heading) is in - the modal is
    // opened once the load succeeds, never empty first.
    this.load(() => this.modalWrapper.openDirective(this.callLogModal));
    this.refreshCallDurations();
  }

  // Defensive catch-up for this customer's RingCentral calls still at 0 duration (same call as the old Call
  // Recordings popup). Runs in the background - loading never waits on it; fixed durations show on the next load.
  // Bounded to the last 2 days (not this tenant's whole history): GetPhoneCallListForDuration/
  // GetCallRecordingDetailsForRingCentral each widen this by a day on either side already, so an unbounded
  // range can push RingCentral's call-log lookup past its 1000-record page and silently miss durations.
  private refreshCallDurations(): void {
    if (!this.tenantId || !this.permissionsReady || !this.callRecordingRights().ViewRight) {
      return;
    }
    const today = new Date();
    const twoDaysAgo = new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000);
    this.calltrackerService.UpdateBatchCallDuration(this.tenantId, formatDate(twoDaysAgo, 'MM/dd/yyyy', 'en-US'), formatDate(today, 'MM/dd/yyyy', 'en-US'))
      .pipe(takeUntil(this.unsubscribeOnDestroy$))
      .subscribe({ error: () => { /* best effort - the grid already shows whatever is stored */ } });
  }

  // X button: save any pending inline edit first, then close - same as the tabs, Audio and Relink.
  close(): void {
    this.commitActiveRow(true, () => this.callLogModal.hide());
  }

  // Fallback for closes that don't go through close() (Esc, backdrop): hiding counts as leaving the edited row.
  onCallLogHide(): void {
    this.commitActiveRow(true);
  }

  setCallType(type: 'All' | 'Calls' | 'Voicemails'): void {
    // Switching tabs counts as leaving the edited row; reload only once its save has finished.
    this.commitActiveRow(true, () => {
      this.callType = type;
      this.load();
    });
  }

  toggleDateSort(): void {
    this.sortDescending = !this.sortDescending;
    this.applyFilters();
  }

  load(onLoaded?: () => void): void {
    if (!this.tenantId) {
      return;
    }
    if (!this.permissionsReady) {
      this.loadRequested = true;
      return;
    }
    this.loadRequested = false;

    // No toast: the host pages disable the Call Log button (with a tooltip) when this right is missing.
    if (!this.callRecordingRights().ViewRight) {
      this.result = null;
      this.rows = [];
      this.loading = false;
      return;
    }

    // The endpoint returns this customer's full (unpaged) history, so the filter row narrows the loaded rows
    // in place instead of round-tripping per keystroke.
    this.loading = true;
    this.communicationCenterService.getProspectCustomerCallLog({
      TenantId: this.tenantId,
      FacilityId: this.facilityId,
      CallType: this.callType
    }).pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (response) => {
        // The shared service types CallDateTime as Date and lacks this popup's extra fields - see the local model.
        this.result = response as unknown as ICustomerCallLogResult;
        this.applyFilters();
        this.loading = false;
        onLoaded?.();
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Failed to load the call log.', 'Error');
      }
    });
  }

  applyFilters(): void {
    const items = this.result?.Items || [];
    const filtered = items.filter(item => this.matchesColumnFilters(item));
    const direction = this.sortDescending ? -1 : 1;
    // By actual instant, not the facility wall-time text, so calls from stores in different timezones order correctly.
    this.rows = filtered.sort((a, b) => ((Date.parse(a.CallDateTime) || 0) - (Date.parse(b.CallDateTime) || 0)) * direction);
  }

  private matchesColumnFilters(item: ICustomerCallLogItem): boolean {
    const f = this.columnFilters;
    const checks: [string, string][] = [
      [f.date, this.formatDateTime(item.CallDateTime)],
      [f.direction, item.Direction],
      [f.duration, this.durationStatus(item) ?? this.formatDuration(item.Duration)],
      [f.sentiment, item.AiSentiment],
      [f.category, this.names(item, 'CallCategories')],
      [f.tags, this.names(item, 'CallTags')],
      [f.agent, item.EmployeeName],
      [f.aiScore, item.AiScore != null ? String(item.AiScore) : ''],
      [f.humanScore, item.HumanScore != null ? String(item.HumanScore) : ''],
      [f.storeName, item.FacilityName],
      [f.adSource, item.AdSourceName],
      // Matches the shown "(555) 240-3391" format as well as the raw stored number.
      [f.callerId, item.PhoneNumber ? `${this.formatCallerId(item.PhoneNumber)} ${item.PhoneNumber}` : ''],
      [f.trackingNumber, item.TrackingNumber ? `${this.formatCallerId(item.TrackingNumber)} ${item.TrackingNumber}` : '']
    ];
    return checks.every(([filterValue, fieldValue]) =>
      !filterValue || (fieldValue || '').toLowerCase().includes(filterValue.trim().toLowerCase()));
  }

  private emptyColumnFilters(): ICallLogColumnFilters {
    return { date: '', direction: '', duration: '', sentiment: '', category: '', tags: '', agent: '', aiScore: '', humanScore: '', storeName: '', adSource: '', callerId: '', trackingNumber: '' };
  }

  private names(item: ICustomerCallLogItem, list: 'CallCategories' | 'CallTags'): string {
    return (item[list] || []).map(x => x.Name).join(', ');
  }

  formatDateTime(value: string): string {
    return this.communicationCenterService.formatCallDateTime(value);
  }

  // Same playable-recording rule as the Call Recordings popup: no recording for missed calls or voicemails
  // (RingCentral doesn't deliver voicemail audio), zero-length calls, 'dum_' placeholder IDs, or Twilio call
  // SIDs ('CA...') whose recording was never fetched.
  hasRecording(item: ICustomerCallLogItem): boolean {
    const recordingId = item.RecordingId || '';
    return !item.MissedCall && !item.VoiceMail && (item.Duration ?? 0) > 0
      && !recordingId.startsWith('dum_')
      && !(item.PhoneSystem === EPhoneSystem.Twilio && recordingId.startsWith('CA'));
  }

  // Duration column status text in place of a time - Voice Mail before Missed Call so a row shows only one.
  durationStatus(item: ICustomerCallLogItem): string | null {
    if (item.VoiceMail) {
      return 'Voice Mail';
    }
    if (item.MissedCall) {
      return 'Missed Call';
    }
    return this.hasRecording(item) ? null : 'No Recording';
  }

  formatDuration(totalSeconds?: number): string {
    return totalSeconds == null ? '-' : formatCallDuration(totalSeconds);
  }

  directionArrow(direction: string): string {
    return callDirectionArrow(direction);
  }

  formatCallerId(phoneNumber: string): string {
    return formatCallerId(phoneNumber);
  }

  sentimentClass(sentiment: string): string {
    return callSentimentClass(sentiment);
  }

  scoreClass(score?: number): string {
    return callScoreClass(score);
  }

  trackByPhoneId(index: number, item: ICustomerCallLogItem): number {
    return item.PhoneId;
  }

  // ---- FR-23 inline categories / tags / Human Score -------------------------------------------------------

  isActiveRow(item: ICustomerCallLogItem): boolean {
    return this.activeRow?.item === item;
  }

  // FR-12/FR-13: only active admin-managed entries, minus the ones already on the call - no freeform entry.
  availableCategories(item: ICustomerCallLogItem): ICallCategory[] {
    return this.callCategories.filter(c => !c.Inactive && !(item.CallCategories || []).some(x => x.Id === c.Id));
  }

  availableTags(item: ICustomerCallLogItem): ICallTag[] {
    return this.callTags.filter(t => !t.Inactive && !(item.CallTags || []).some(x => x.Id === t.Id));
  }

  openCategoryPicker(item: ICustomerCallLogItem): void {
    this.openRowPicker(item, 'category');
  }

  openTagPicker(item: ICustomerCallLogItem): void {
    this.openRowPicker(item, 'tag');
  }

  private openRowPicker(item: ICustomerCallLogItem, kind: 'category' | 'tag'): void {
    if (!this.beginRowEdit(item)) {
      return;
    }
    this.openPicker = kind;
    this.cdr.detectChanges();
    const select = this.rowPicker?.nativeElement;
    select?.focus();
    try {
      // Opens the option list straight away like the mockup (image-171/172) where the browser supports it.
      (select as HTMLSelectElement & { showPicker?: () => void })?.showPicker?.();
    } catch {
      // Not supported or not allowed here - the focused select still opens on the next click.
    }
  }

  closePicker(): void {
    this.openPicker = null;
  }

  pickCategory(item: ICustomerCallLogItem, categoryId: number | null): void {
    const category = this.callCategories.find(c => c.Id === categoryId);
    if (category) {
      item.CallCategories = [...(item.CallCategories || []), { Id: category.Id, Name: category.Name }];
    }
    this.openPicker = null;
  }

  pickTag(item: ICustomerCallLogItem, tagId: number | null): void {
    const tag = this.callTags.find(t => t.Id === tagId);
    if (tag) {
      item.CallTags = [...(item.CallTags || []), { Id: tag.Id, Name: tag.Name }];
    }
    this.openPicker = null;
  }

  removeCategory(item: ICustomerCallLogItem, categoryId: number): void {
    if (this.beginRowEdit(item)) {
      item.CallCategories = item.CallCategories.filter(c => c.Id !== categoryId);
    }
  }

  removeTag(item: ICustomerCallLogItem, tagId: number): void {
    if (this.beginRowEdit(item)) {
      item.CallTags = item.CallTags.filter(t => t.Id !== tagId);
    }
  }

  openScoreEditor(item: ICustomerCallLogItem): void {
    if (!this.beginRowEdit(item)) {
      return;
    }
    this.scoreEditing = true;
    this.scoreValue = item.HumanScore ?? null;
    this.scoreBlocked = false;
    this.cdr.detectChanges();
    this.scoreInput?.nativeElement.focus();
  }

  // Digits only: letters, '-', '+', '.', 'e' are refused and raise the range alert; editing keys and shortcuts
  // (except paste) still work. Any digit/delete keystroke, or leaving the row, clears an earlier refused-key alert.
  onScoreKeydown(event: KeyboardEvent): void {
    const isShortcut = event.ctrlKey || event.metaKey;
    if (isShortcut && event.key.toLowerCase() !== 'v') {
      return;
    }
    if (/^\d$/.test(event.key) || (!isShortcut && SCORE_EDIT_KEYS.includes(event.key))) {
      if (event.key !== 'Tab') {
        this.scoreBlocked = false;
      }
      return;
    }
    event.preventDefault();
    this.scoreBlocked = true;
  }

  // Keeps the score within 100 by dropping trailing digits as they're typed (e.g. 888 -> 88).
  onScoreInput(input: HTMLInputElement): void {
    let digits = input.value;
    while (digits.length > 0 && Number(digits) > 100) {
      digits = digits.slice(0, -1);
    }
    if (digits !== input.value) {
      input.value = digits;
      this.scoreValue = digits === '' ? null : Number(digits);
    }
  }

  // No pasting or dropping text into the score - it has to be typed.
  blockScorePaste(event: Event): void {
    event.preventDefault();
    this.scoreBlocked = true;
  }

  // Drives the range alert: a refused key/paste, or a score outside 0-100.
  get scoreInvalid(): boolean {
    return this.scoreBlocked || this.scoreOutOfRange;
  }

  // Only a genuinely bad value holds the row open. A refused key never reaches the input, so its alert alone must
  // not stop the user leaving the row.
  private get scoreOutOfRange(): boolean {
    return this.scoreValue != null && (!Number.isInteger(this.scoreValue) || this.scoreValue < 0 || this.scoreValue > 100);
  }

  // Starts (or continues) inline editing on this row, snapshotting its saved values the first time. Refused when the
  // user lacks Edit, or while another row is held open by an invalid Human Score.
  private beginRowEdit(item: ICustomerCallLogItem): boolean {
    if (!this.editRight) {
      return false;
    }
    if (this.activeRow && this.activeRow.item !== item) {
      return false;
    }
    if (!this.activeRow) {
      this.activeRow = {
        item,
        savedCategories: [...(item.CallCategories || [])],
        savedTags: [...(item.CallTags || [])],
        savedHumanScore: item.HumanScore ?? null
      };
    }
    return true;
  }

  // A mousedown anywhere outside the row being edited counts as leaving it - except on [data-commits-row] controls
  // (tabs, Audio, Relink, close). Their click handlers save the row and wait for it themselves; saving here first
  // would put the loader over them before mouseup (losing the click) or let a tab reload race the save.
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.activeRow) {
      return;
    }
    const target = event.target as Element;
    if (target?.closest?.('[data-commits-row]')) {
      return;
    }
    const row = target?.closest?.('tr[data-phone-id]');
    if (row && Number(row.getAttribute('data-phone-id')) === this.activeRow.item.PhoneId) {
      return;
    }
    this.commitActiveRow(false);
  }

  // Saves the edited row through the single SaveProspectCustomerCallLogRow API - only if its categories, tags or
  // Human Score differ from the last saved values. An invalid Human Score holds the row open (no save) until fixed;
  // when the popup closes or the tab switches (leaving = true) such a row's unsaved edits are dropped instead.
  private commitActiveRow(leaving: boolean, afterwards?: () => void): void {
    const active = this.activeRow;
    if (!active) {
      afterwards?.();
      return;
    }

    if (this.scoreEditing && this.scoreOutOfRange) {
      if (!leaving) {
        return;
      }
      this.restoreRow(active);
      this.resetEditState();
      afterwards?.();
      return;
    }

    const item = active.item;
    const humanScore = this.scoreEditing ? this.scoreValue : item.HumanScore ?? null;
    this.resetEditState();

    // Each part is validated on its own server-side, so one can fail while the others save - the toasts report both.
    const changedFields: CallLogRowPart[] = [];
    if (!this.sameIds(item.CallCategories, active.savedCategories)) {
      changedFields.push('Category/Reason');
    }
    if (!this.sameIds(item.CallTags, active.savedTags)) {
      changedFields.push('Tags');
    }
    if (humanScore !== active.savedHumanScore) {
      changedFields.push('Human Score');
    }
    if (changedFields.length === 0) {
      afterwards?.();
      return;
    }

    item.HumanScore = humanScore;
    this.loading = true;
    this.communicationCenterService.saveProspectCustomerCallLogRow({
      PhoneId: item.PhoneId,
      CallCategoryIds: (item.CallCategories || []).map(c => c.Id),
      CallTagIds: (item.CallTags || []).map(t => t.Id),
      HumanScore: humanScore
    }).pipe(takeUntil(this.unsubscribeOnDestroy$)).subscribe({
      next: (response) => {
        this.loading = false;
        const failed = changedFields.filter(part => this.partError(response, part));
        if (!response?.Success && failed.length === 0) {
          // Whole-row failure (e.g. call not found) - nothing was saved.
          this.reportRowSaveFailure(active, changedFields, response?.Message);
        } else {
          // Saved parts already show their new values - no reload, so the grid keeps its scroll position.
          const saved = changedFields.filter(part => !failed.includes(part));
          if (saved.length) {
            this.toastr.success(`${this.joinFields(saved)} saved Successfully`, 'Success');
          }
          failed.forEach(part => this.restorePart(active, part));
          if (saved.length === 0) {
            // Nothing saved - one combined toast, same as a whole-row failure.
            this.toastr.error(`Failed to save ${this.joinFields(failed)}`, 'Error');
          } else {
            failed.forEach(part => this.toastr.error(`Failed to save ${part}`, 'Error'));
          }
        }
        afterwards?.();
      },
      error: () => {
        this.loading = false;
        this.reportRowSaveFailure(active, changedFields, null);
        afterwards?.();
      }
    });
  }

  private reportRowSaveFailure(active: ICallLogActiveRow, changedFields: CallLogRowPart[], message: string | null): void {
    changedFields.forEach(part => this.restorePart(active, part));
    this.toastr.error(`Failed to save ${this.joinFields(changedFields)}${message ? `: ${message}` : ''}`, 'Error');
  }

  private partError(response: ICustomerCallLogRowSaveResult, part: CallLogRowPart): string | undefined {
    switch (part) {
      case 'Category/Reason': return response?.CategoryError;
      case 'Tags': return response?.TagError;
      default: return response?.HumanScoreError;
    }
  }

  // ['Tags'] -> 'Tags'; ['Category/Reason', 'Tags', 'Human Score'] -> 'Category/Reason, Tags and Human Score'.
  private joinFields(fields: string[]): string {
    return fields.length > 1 ? `${fields.slice(0, -1).join(', ')} and ${fields[fields.length - 1]}` : fields[0];
  }

  private restoreRow(active: ICallLogActiveRow): void {
    (['Category/Reason', 'Tags', 'Human Score'] as CallLogRowPart[]).forEach(part => this.restorePart(active, part));
  }

  private restorePart(active: ICallLogActiveRow, part: CallLogRowPart): void {
    switch (part) {
      case 'Category/Reason': active.item.CallCategories = active.savedCategories; break;
      case 'Tags': active.item.CallTags = active.savedTags; break;
      default: active.item.HumanScore = active.savedHumanScore;
    }
  }

  private resetEditState(): void {
    this.activeRow = null;
    this.openPicker = null;
    this.scoreEditing = false;
    this.scoreValue = null;
    this.scoreBlocked = false;
  }

  private sameIds(current: ICommCallCategoryOrTagAssociation[], saved: ICommCallCategoryOrTagAssociation[]): boolean {
    const currentIds = (current || []).map(x => x.Id).sort((a, b) => a - b);
    const savedIds = (saved || []).map(x => x.Id).sort((a, b) => a - b);
    return currentIds.length === savedIds.length && currentIds.every((id, i) => id === savedIds[i]);
  }

  // ---- Call Detail / Relink ---------------------------------------------------------------------------------

  // Only calls with a playable recording have the Audio button, and only they open the detail popup.
  openCallDetail(item: ICustomerCallLogItem): void {
    if (!this.hasRecording(item)) {
      return;
    }
    // Save any pending edit on this row first, so the detail popup shows the same values the grid does.
    this.commitActiveRow(true, () => {
      // The page loader covers the detail's own load; the popup opens once it's in (onCallDetailLoaded).
      this.loading = true;
      this.selectedCall = item;
    });
  }

  // Swaps the Call Log popup for the detail popup (same as Relink) rather than stacking them.
  onCallDetailLoaded(): void {
    this.loading = false;
    this.callLogModal.hide();
    this.modalWrapper.openDirective(this.callDetailModal);
  }

  closeCallDetail(): void {
    this.callDetailModal.hide();
  }

  // Dropping the detail component on close also stops any audio still playing. The Call Log popup comes back as it
  // was (tab, filters, scroll) - the detail popup is read-only, so the grid needs no reload.
  onCallDetailHidden(): void {
    this.selectedCall = null;
    // The page loader covers the gap until the Call Log popup has finished showing again (onCallLogShown).
    this.loading = true;
    this.modalWrapper.openDirective(this.callLogModal);
  }

  onCallLogShown(): void {
    this.loading = false;
  }

  // Same Relink flow as the Call Recording popup - RelinkCallComponent reads the call-recording grid's field
  // names (CallerID, RecordingID, CallDuration), so the call log row is mapped onto them here.
  onRelinkClick(item: ICustomerCallLogItem): void {
    // In this popup Relink needs Customer Call Recordings Edit, same as the inline Category/Tags/Human Score edits
    // (the shared RelinkCallComponent and other screens are not gated).
    if (!this.editRight) {
      return;
    }
    // Save any pending inline edit first and open Relink only once it's saved - same as the tabs and Audio.
    this.commitActiveRow(true, () => {
      this.loading = true;
      this.storeService.getStores().subscribe({
        next: (stores) => {
          this.loading = false;
          this.callLogModal.hide();
          this.relinkModalRef = this.modalWrapper.openComponent(RelinkCallComponent, {
            initialState: {
              callData: {
                ...item,
                CallerID: item.PhoneNumber,
                RecordingID: item.RecordingId,
                CallDuration: item.Duration,
                // RelinkCallComponent.cancel() calls parentModal.show() - reopen through the modal wrapper
                // instead so the body scroll/padding fix runs and the page doesn't flicker.
                parentModal: { show: () => this.modalWrapper.openDirective(this.callLogModal) }
              },
              allStores: stores
            },
            class: 'modal-xl modal-dialog-centered',
            backdrop: 'static',
            keyboard: false
          });

          this.relinkModalRef.content.onRelinkSuccess.pipe(take(1)).subscribe((relinkResult) => {
            // Reopen only once the reload is in (same as the first open), so the loader stays up and no pre-relink
            // rows show. A failed reload toasts and leaves the popup closed, also same as the first open.
            this.load(() => this.modalWrapper.openDirective(this.callLogModal));
            if (relinkResult?.success) {
              this.toastr.success('Relink call was successful', 'Success');
            } else {
              this.toastr.error('Failed to relink call', 'Failure');
            }
          });
        },
        error: () => {
          this.loading = false;
        }
      });
    });
  }
}
